/**
 * 浏览器原生通知封装
 * 使用浏览器 Notification API 实现桌面通知
 */

/**
 * 检查浏览器是否支持通知
 */
export const isSupported = () => {
    return 'Notification' in window;
};

/**
 * 检查是否已获得通知权限
 * @returns {Promise<boolean>}
 */
export const hasPermission = async () => {
    if (!isSupported()) return false;
    
    const permission = await Notification.requestPermission();
    return permission === 'granted';
};

/**
 * 请求通知权限
 * @returns {Promise<boolean>} 是否获得权限
 */
export const requestPermission = async () => {
    if (!isSupported()) {
        console.warn('当前浏览器不支持通知功能');
        return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
};

/**
 * 获取当前权限状态
 * @returns {string} 'granted' | 'denied' | 'default'
 */
export const getCurrentPermission = () => {
    if (!isSupported()) return 'unsupported';
    return Notification.permission;
};

/**
 * 创建并显示通知
 * @param {Object} options - 通知配置
 * @param {string} options.title - 通知标题
 * @param {string} [options.body] - 通知内容
 * @param {string} [options.icon] - 通知图标URL
 * @param {string} [options.image] - 通知图片URL
 * @param {string} [options.badge] - 通知徽章图标URL
 * @param {boolean} [options.silent=false] - 是否静音
 * @param {number} [options.requireInteraction=false] - 是否需要用户交互才能关闭
 * @param {number} [options.timeout=5000] - 自动关闭时间（毫秒），0表示不自动关闭
 * @param {Function} [options.onClick] - 点击通知回调
 * @param {Function} [options.onClose] - 关闭通知回调
 * @param {Function} [options.onError] - 通知错误回调
 * @param {Function} [options.onShow] - 通知显示回调
 * @returns {Notification|null} 返回通知实例
 */
export const showNotification = async (options) => {
    if (!isSupported()) {
        console.warn('[Notification] 当前浏览器不支持通知功能');
        return null;
    }

    const {
        title,
        body = '',
        icon = '',
        image = '',
        badge = '',
        silent = false,
        requireInteraction = false,
        timeout = 5000,
        onClick = null,
        onClose = null,
        onError = null,
        onShow = null
    } = options;

    if (!title) {
        console.error('[Notification] 通知标题(title)不能为空');
        return null;
    }

    // 检查权限
    const permission = await Notification.requestPermission();
    console.log('[Notification] 当前权限状态:', permission);
    
    if (permission !== 'granted') {
        console.warn('[Notification] 用户未授权通知权限，请先在浏览器设置中开启通知权限');
        console.warn('[Notification] 权限状态:', permission);
        return null;
    }

    // 构建通知选项
    const notificationOptions = {
        body,
        icon,
        image,
        badge,
        silent,
        requireInteraction
    };

    try {
        const notification = new Notification(title, notificationOptions);
        
        console.log('[Notification] 通知实例创建成功，等待显示...');

        // 绑定事件
        if (typeof onClick === 'function') {
            notification.onclick = (event) => {
                event.preventDefault();
                onClick(notification);
            };
        }

        if (typeof onClose === 'function') {
            notification.onclose = () => onClose(notification);
        }

        if (typeof onError === 'function') {
            notification.onerror = (event) => onError(event, notification);
        }

        // 添加默认的 onshow 和 onerror 事件以便调试
        const originalOnShow = notification.onshow;
        notification.onshow = () => {
            if (typeof onShow === 'function') {
                onShow(notification);
            }
            if (typeof originalOnShow === 'function') {
                originalOnShow();
            }
        };

        const originalOnError = notification.onerror;
        notification.onerror = (event) => {
            console.error('[Notification] 通知显示失败:', event);
            if (typeof onError === 'function') {
                onError(event, notification);
            }
            if (typeof originalOnError === 'function') {
                originalOnError(event);
            }
        };

        // 设置自动关闭
        if (timeout > 0) {
            setTimeout(() => {
                if (notification) {
                    notification.close();
                }
            }, timeout);
        }

        return notification;
    } catch (error) {
        console.error('[Notification] 创建通知失败:', error);
        return null;
    }
};

/**
 * 显示简单通知（简化版）
 * @param {string} title - 标题
 * @param {string} [message] - 消息内容
 * @param {string} [icon] - 图标
 * @returns {Promise<Notification|null>}
 */
export const notify = async (title, message = '', icon = '') => {
    return showNotification({
        title,
        body: message,
        icon,
        timeout: 5000
    });
};

/**
 * 显示成功通知
 * @param {string} message - 消息内容
 * @param {string} [title='成功'] - 标题
 * @returns {Promise<Notification|null>}
 */
export const success = async (message, title = '成功') => {
    return showNotification({
        title,
        body: message,
        icon: 'https://images.xianyouhe.com/kefu/new-kefu/pc/success.png',
        timeout: 3000
    });
};

/**
 * 显示错误通知
 * @param {string} message - 消息内容
 * @param {string} [title='错误'] - 标题
 * @returns {Promise<Notification|null>}
 */
export const error = async (message, title = '错误') => {
    return showNotification({
        title,
        body: message,
        icon: 'https://images.xianyouhe.com/kefu/new-kefu/pc/error.png',
        timeout: 5000
    });
};

/**
 * 显示警告通知
 * @param {string} message - 消息内容
 * @param {string} [title='警告'] - 标题
 * @returns {Promise<Notification|null>}
 */
export const warning = async (message, title = '警告') => {
    return showNotification({
        title,
        body: message,
        icon: 'https://images.xianyouhe.com/kefu/new-kefu/pc/warning.png',
        timeout: 4000
    });
};

/**
 * 显示信息通知
 * @param {string} message - 消息内容
 * @param {string} [title='提示'] - 标题
 * @returns {Promise<Notification|null>}
 */
export const info = async (message, title = '提示') => {
    return showNotification({
        title,
        body: message,
        icon: 'https://images.xianyouhe.com/kefu/new-kefu/pc/info.png',
        timeout: 4000
    });
};

/**
 * 检查当前标签页是否可见（用户是否正在浏览当前页面）
 * @returns {boolean} true表示当前标签页可见，false表示已切换到其他标签页或最小化
 */
export const isPageVisible = () => {
    return document.visibilityState === 'visible';
};

/**
 * 检查当前标签页是否隐藏
 * @returns {boolean} true表示当前标签页已隐藏
 */
export const isPageHidden = () => {
    return document.visibilityState === 'hidden';
};

/**
 * 监听标签页可见性变化
 * @param {Function} onVisible - 标签页变为可见时的回调
 * @param {Function} onHidden - 标签页变为隐藏时的回调
 * @returns {Function} 返回取消监听的函数
 */
export const onVisibilityChange = (onVisible, onHidden) => {
    const handler = () => {
        if (document.visibilityState === 'visible') {
            if (typeof onVisible === 'function') onVisible();
        } else if (document.visibilityState === 'hidden') {
            if (typeof onHidden === 'function') onHidden();
        }
    };

    document.addEventListener('visibilitychange', handler);

    // 返回取消监听的函数
    return () => {
        document.removeEventListener('visibilitychange', handler);
    };
};

// 默认导出所有方法
export default {
    isSupported,
    hasPermission,
    getCurrentPermission,
    requestPermission,
    showNotification,
    notify,
    success,
    error,
    warning,
    info,
    isPageVisible,
    isPageHidden,
    onVisibilityChange
};
